import { Lot, Reserve, StatisticsGroupBy } from "../common/types/Types";
import {
  useOperation,
  useOperationLots,
  useOperationReserves,
} from "../api/hooks";
import Loader from "../common/views/Loader";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { EntitySelect } from "../common/inputs/EntitySelect";
import { BYTable } from "../common/views/BYTable";
import { AuthContext } from "../providers/AuthProvider";
import { getFormattedDataTable } from "./getFormattedDataTable";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import BYExportButtonsTable from "../common/buttons/BYExportButtonsTable";

interface StatisticsViewProps {
  operationId: number;
}

const COLORS_PIE = ["#86bc24", "#003849"];
const COLORS = ["#86bc24", "#0088FE", "#FFBB28", "#FF8042"];

export const StatisticsView = ({ operationId }: StatisticsViewProps) => {
  const { data: reserves = [], isLoading: isLoadingReserves } =
    useOperationReserves(operationId);
  const { data: operation, isLoading: isLoadingOperation } =
    useOperation(operationId);
  const { data: lots = [] as Lot[] } = useOperationLots(operationId);

  const { formatter } = useContext(AuthContext);

  const isLoading = isLoadingReserves || isLoadingOperation;

  const [filteredReserves, setFilteredReserves] = useState<Reserve[]>([]);
  const [selectedBuildings, setSelectedBuildings] = useState<string[]>([]);
  const [selectedPhases, setSelectedPhases] = useState<number[]>([]);
  const [groupBy, setGroupBy] = useState<StatisticsGroupBy>("phase");

  reserves.forEach((r) => {
    r.phase = {
      refPhase: r.phaseRefPhase,
      libPhase: formatter.getPhaseDescription(r.phaseRefPhase),
    };
  });

  useEffect(() => {
    const newReserves = reserves
      .filter((reserve) => {
        return !reserve.indicRas && !reserve.indicSupprime;
      })
      .filter((reserve) => {
        const hasBuilding =
          !selectedBuildings.length ||
          selectedBuildings.includes(reserve.lot?.batiment);
        const hasPhase =
          !selectedPhases.length ||
          selectedPhases.includes(reserve.phaseRefPhase);

        return hasBuilding && hasPhase;
      });

    setFilteredReserves(newReserves);
  }, [selectedBuildings, selectedPhases, reserves]);

  const { rows, totalRow, titleRow } = useMemo(
    () => getFormattedDataTable(filteredReserves, lots, groupBy),
    [filteredReserves, lots, groupBy]
  );

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center flex-grow-1 my-15">
        <Loader />
      </div>
    );
  }

  const columns = Array.from({ length: titleRow.length }, (v, i) => i);

  const barData = rows.map((row) => ({
    name: row[0],
    "Non levée": row[13],
    Réactivée: row[12],
    Levée: Number(row[11]) - Number(row[12]) - Number(row[13]),
  }));

  return (
    <>
      <div className="row mb-3">
        <div className="col-3">
          <EntitySelect
            type="statistics-groupby"
            title="Aggrégation"
            onChange={(value) => setGroupBy(value)}
            value={groupBy}
          />
        </div>
        {groupBy !== "phase" && (
          <div className="col-3">
            <EntitySelect
              type="phase"
              title="Phase"
              isMulti={true}
              onChange={(value) => setSelectedPhases(value)}
              value={selectedPhases}
            />
          </div>
        )}
        <div className="col-3">
          <EntitySelect
            type="buildings"
            isMulti
            title="Bâtiment"
            placeholder="Tous les Bâtiment"
            operationId={operationId}
            onChange={(value) => setSelectedBuildings(value)}
            value={selectedBuildings}
          />
        </div>
        <div
          className={
            groupBy !== "phase"
              ? "col-3 statistics-export-buttons-container"
              : "col-6 statistics-export-buttons-container"
          }
        >
          <div className="">
            <p className="mr-2">Exporter</p>
            <BYExportButtonsTable
              data={[titleRow, ...rows, totalRow]}
              title={`Statistiques ${operation?.nomOperation}`}
            />
          </div>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="d-block my-4">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              width={500}
              height={300}
              data={barData}
              margin={{
                top: 30,
                right: 60,
                left: 60,
                bottom: 100,
              }}
            >
              <Legend layout="horizontal" verticalAlign="top" />
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                fontSize={11}
                textAnchor="end"
                interval={0}
                angle={-25}
              />
              <YAxis />
              <Tooltip />
              <Bar dataKey="Non levée" stackId="a" fill={COLORS_PIE[1]} />
              <Bar dataKey="Levée" stackId="a" fill={COLORS_PIE[0]} />
              <Bar dataKey="Réactivée" stackId="a" fill={COLORS[3]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <BYTable
        className="center-content statistics-container"
        loading={isLoading}
        data={rows}
        columns={columns}
        columnNames={titleRow}
        withPaging={false}
        theadContent={
          <tr className="table-light border-bottom-0">
            <th className="bg-light"></th>
            <th colSpan={6} className="bg-info">
              Réserves Logements
            </th>
            <th colSpan={4} className="bg-primary">
              Parties Communes
            </th>
            <th colSpan={4} className="bg-warning">
              Total
            </th>
          </tr>
        }
        sortDataTransform={{
          0: (row) => {
            if (groupBy === "phase") {
              const phase = row[0];

              const PHASES_ORDER = {
                "Visite Cloison": 1,
                OPR: 2,
                "Pré-livraison": 3,
                Livraison: 4,
                "Réserve à 1 mois": 5,
                "Garantie de parfait achèvement": 6,
                "Garantie de bon fonctionnement": 7,
                Décennale: 8,
              };

              return PHASES_ORDER[phase];
            }
          },
        }}
        tbodyContent={
          <tr className="table-dark">
            {totalRow.map((content, i) => (
              <th key={`tf-statistics-${i}`}>{content}</th>
            ))}
            ;
          </tr>
        }
        height={60}
        keyExtractor={(r) => `statistics-row-${r[0]}`}
      />
    </>
  );
};
